import { useMemo, useState } from 'react';
import { usePermissions } from '@/hooks/usePermissions';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import { useConfirm } from '@/hooks/useConfirm';
import { useCertificates } from '@/hooks/data/useCertificates';
import { useDomains } from '@/hooks/data/useDomains';
import { useNodes } from '@/hooks/data/useNodes';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { PlusIcon, RadarIcon, SyncIcon } from '@/components/icons';
import {
  ClearFiltersButton,
  ErrorAlert,
  PageHeader,
  SearchField,
} from '@/components/PageParts';
import { CertificateDetailsModal } from '@/components/CertificateDetailsModal';
import {
  CertificatesTable,
  type CertificateRowAction,
} from '@/components/certificates/CertificatesTable';
import { IssueCertificateModal } from '@/components/certificates/IssueCertificateModal';
import { ImportCertificateModal } from '@/components/certificates/ImportCertificateModal';
import { MonitoringResultsModal } from '@/components/certificates/MonitoringResultsModal';
import { certificateStatusMap } from '@/config/statusMaps';
import { domainRules, getCertificateActions } from '@/config/resourceRules';
import { nodeHasCapability } from '@/config/nodeTypes';
import { pluralize } from '@/utils/format';
import type { Certificate, CertificateStatus, MonitorSummaryResponse } from '@/types/certificate';
import './Certificates.css';

type BusyRowAction = Extract<CertificateRowAction, 'sync' | 'renew' | 'retry'>;

const ROW_ACTION_ERRORS: Record<BusyRowAction, string> = {
  sync: 'Failed to sync certificate',
  renew: 'Failed to renew certificate',
  retry: 'Failed to retry certificate',
};

/**
 * Certificates page component
 *
 * Features:
 * - List all certificates with filtering
 * - Issue / import / renew / retry / sync certificates (operator+)
 * - Delete certificates (admin only)
 * - Status and expiration indicators
 */
export function Certificates() {
  const { role, can } = usePermissions();

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<CertificateStatus | ''>('');
  const [expiringSoonFilter, setExpiringSoonFilter] = useState(false);

  const {
    certificates,
    isLoading,
    error: loadError,
    reload,
    issueCertificate,
    importCertificate,
    renewCertificate,
    retryCertificate,
    syncCertificate,
    deleteCertificate,
    runMonitoring,
  } = useCertificates({
    status: statusFilter || undefined,
    expiring_soon: expiringSoonFilter || undefined,
  });

  // Lookups for names and modal options (failures are non-critical)
  const { domains } = useDomains();
  const { nodes } = useNodes();

  // UI state
  const [openModal, setOpenModal] = useState<'issue' | 'import' | null>(null);
  const [viewedCert, setViewedCert] = useState<Certificate | null>(null);
  const [monitoringResult, setMonitoringResult] = useState<MonitorSummaryResponse | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const { confirm, confirmDialog } = useConfirm();

  const rowAction = useAsyncAction(
    (action: BusyRowAction, cert: Certificate) => {
      switch (action) {
        case 'sync':
          return syncCertificate(cert.id);
        case 'renew':
          return renewCertificate(cert.id);
        case 'retry':
          return retryCertificate(cert.id);
      }
    },
    {
      errorMessage: (action) => ROW_ACTION_ERRORS[action],
      getKey: (action, cert) => `${action}:${cert.id}`,
      onError: setActionError,
    }
  );

  const monitoring = useAsyncAction(runMonitoring, {
    errorMessage: 'Failed to run certificate monitoring',
    onError: setActionError,
  });

  const domainNames = useMemo(() => new Map(domains.map((d) => [d.id, d.name])), [domains]);
  const nodeNames = useMemo(() => new Map(nodes.map((n) => [n.id, n.name])), [nodes]);
  const getDomainName = (domainId: string) => domainNames.get(domainId) ?? 'Unknown';
  const getNodeName = (nodeId: string | null) => (nodeId ? nodeNames.get(nodeId) ?? 'Unknown' : '-');

  /**
   * Filter certificates by search query (domain or common name)
   */
  const query = searchQuery.trim().toLowerCase();
  const filteredCertificates = query
    ? certificates.filter(
        (cert) =>
          (domainNames.get(cert.domain_id) ?? '').toLowerCase().includes(query) ||
          cert.common_name.toLowerCase().includes(query)
      )
    : certificates;

  const eligibleDomains = domains.filter(domainRules.canIssueCertificate);
  const importDomains = domains.filter(domainRules.isAssigned);
  const importNodes = nodes.filter((node) => nodeHasCapability(node, 'importsCertificates'));

  const handleRowAction = (action: CertificateRowAction, cert: Certificate) => {
    setActionError(null);
    switch (action) {
      case 'view':
        setViewedCert(cert);
        return;
      case 'delete':
        void confirm({
          title: 'Delete Certificate',
          message: (
            <>
              Are you sure you want to delete the certificate for{' '}
              <strong>{cert.common_name}</strong>? This will revoke the certificate and cannot be
              undone.
            </>
          ),
          confirmLabel: 'Delete',
          onConfirm: () => deleteCertificate(cert.id),
          errorMessage: 'Failed to delete certificate',
        });
        return;
      default:
        void rowAction.run(action, cert);
    }
  };

  const handleTriggerMonitoring = async () => {
    setActionError(null);
    const result = await monitoring.run();
    if (result.ok) setMonitoringResult(result.value);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setExpiringSoonFilter(false);
  };

  const handleRetry = () => {
    setActionError(null);
    void reload();
  };

  const hasActiveFilters = Boolean(searchQuery || statusFilter || expiringSoonFilter);
  const canIssue = can('certificates:issue');

  return (
    <div className="certs-page">
      <PageHeader
        classPrefix="certs"
        title="TLS Certificates"
        count={pluralize(filteredCertificates.length, 'certificate')}
        wrapActions
        actions={
          (can('certificates:monitor') || canIssue || can('certificates:import')) && (
            <>
              {can('certificates:monitor') && (
                <Button
                  variant="secondary"
                  leftIcon={<RadarIcon />}
                  onClick={handleTriggerMonitoring}
                  isLoading={monitoring.isPending()}
                >
                  Check for Updates
                </Button>
              )}
              {can('certificates:import') && importNodes.length > 0 && (
                <Button variant="secondary" leftIcon={<SyncIcon />} onClick={() => setOpenModal('import')}>
                  Import from Plesk
                </Button>
              )}
              {canIssue && (
                <Button leftIcon={<PlusIcon />} onClick={() => setOpenModal('issue')}>
                  Issue Certificate
                </Button>
              )}
            </>
          )
        }
      />

      {/* Filters */}
      <div className="certs-filters">
        <SearchField
          classPrefix="certs"
          placeholder="Search by domain..."
          value={searchQuery}
          onChange={setSearchQuery}
        />

        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          placeholder="All Status"
          options={certificateStatusMap.options}
          className="certs-filter-select"
          aria-label="Filter by status"
        />

        <label className="certs-filter-checkbox">
          <input
            type="checkbox"
            checked={expiringSoonFilter}
            onChange={(e) => setExpiringSoonFilter(e.target.checked)}
          />
          <span>Expiring soon</span>
        </label>

        {hasActiveFilters && <ClearFiltersButton classPrefix="certs" onClick={handleClearFilters} />}
      </div>

      <ErrorAlert classPrefix="certs" message={actionError ?? loadError} onRetry={handleRetry} />

      <CertificatesTable
        certificates={filteredCertificates}
        isLoading={isLoading}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={handleClearFilters}
        onIssueFirst={canIssue ? () => setOpenModal('issue') : undefined}
        getDomainName={getDomainName}
        getNodeName={getNodeName}
        getActions={(cert) => getCertificateActions(role, cert)}
        isBusy={(action, certId) => rowAction.isPending(`${action}:${certId}`)}
        onAction={handleRowAction}
      />

      {openModal === 'issue' && (
        <IssueCertificateModal
          isOpen
          onClose={() => setOpenModal(null)}
          domains={eligibleDomains}
          onIssue={issueCertificate}
        />
      )}

      {openModal === 'import' && (
        <ImportCertificateModal
          isOpen
          onClose={() => setOpenModal(null)}
          domains={importDomains}
          nodes={importNodes}
          onImport={importCertificate}
        />
      )}

      {viewedCert && (
        <CertificateDetailsModal
          certificateId={viewedCert.id}
          commonName={viewedCert.common_name}
          onClose={() => setViewedCert(null)}
        />
      )}

      <MonitoringResultsModal result={monitoringResult} onClose={() => setMonitoringResult(null)} />

      {confirmDialog}
    </div>
  );
}
